import styled from '@emotion/styled';
import { themeGet } from '@styled-system/theme-get';
import { StyledMenuItem } from './MenuItem/styles';

export const StyledLogo = styled.figure`
  grid-area: logo;
  padding: ${themeGet('scale.scaleUp03')};
  background: ${themeGet('color.colorWhite')};
  z-index: ${themeGet('zIndex.zIndex02')};

  .nav-logo-link {
    display: inline-block;
  }
  
  svg {
    height: ${themeGet('scale.scaleUp03')};
    width: auto;
    display: block;
    margin: 0;
  }

  .is-mobile-open & {
    position: sticky;
    top: 0;
  }

  @media (min-width: ${themeGet('breakpoint.breakpointMobile')}) {
    padding: ${themeGet('scale.scaleUp06')} ${themeGet('scale.scaleUp07')};

    svg {
      height: ${themeGet('scale.scaleUp05')};
    }
  }

  @media (min-width: ${themeGet('breakpoint.breakpointNavMd')}) {
    padding: ${themeGet('scale.scaleUp06')} ${themeGet('scale.scaleUp05')};
    padding-right: ${themeGet('scale.scaleUp02')};
    background: none;
    svg {
      max-height: ${themeGet('scale.scaleUp05')};
    }
  }

  @media (min-width: ${themeGet('breakpoint.breakpointNavXl')}) {
    padding: ${themeGet('scale.scaleUp03')};
  }
`;

export const StyledMenuBtn = styled.div`
  grid-area: menu-button;
  padding-right: ${themeGet('scale.scaleUp03')};
  display: flex;
  align-content: center;
  justify-content: flex-end;
  background: ${themeGet('color.colorWhite')};
  z-index: ${themeGet('zIndex.zIndex02')};

  button {
    ${themeGet('font.whitneySemibold')}
    border: none;
    background: none;
    padding: 0;
    font-size: ${themeGet('scale.scaleBase')};
    cursor: pointer;
  }

  svg {
    max-width: ${themeGet('scale.scaleDown01')};
    height: auto;
    display: block;
  }

  .is-mobile-open & {
    top: 0;
    position: sticky;
  }

  @media (min-width: ${themeGet('breakpoint.breakpointMobile')}) {
    padding-right: ${themeGet('scale.scaleUp07')};

    svg {
      max-width: ${themeGet('scale.scaleBase')};
    }

    button {
      font-size: ${themeGet('scale.scaleUp01')};
    }
  }

  @media (min-width: ${themeGet('breakpoint.breakpointNavMd')}) {
    display: none;
  }
`;

export const StyledMainMenu = styled.div`
  grid-area: main-menu;
  display: none;
  justify-content: center;

  @media (min-width: ${themeGet('breakpoint.breakpointNavMd')}) {
    display: flex;
  }
`;

export const StyledSecondaryMenu = styled.div`
  grid-area: secondary-menu;
  padding-bottom: ${themeGet('scale.scaleUp12')};
  display: none;

  ${StyledMenuItem} {
    > a,
    > button {
      color: #1F304C;

      > svg path {
        fill: ${themeGet('color.colorGrayBlue60')};
      }
    }

    &.is-open,
    &.is-active {
      > a,
      > button {
        color: ${themeGet('color.colorBlue60')};

        > svg path {
          fill: ${themeGet('color.colorBlue60')};
        }
      }
    }
  }

  @media (min-width: ${themeGet('breakpoint.breakpointMobile')}) {
    padding-bottom: 130px;
  }

  @media (min-width: ${themeGet('breakpoint.breakpointNavMd')}) {
    display: flex;
    padding-bottom: 0;
  }
`;

export const StyledCta = styled.div`
  grid-area: cta;
  display: none;
  position: fixed;
  bottom: 0;
  width: 100%;
  padding: ${themeGet('scale.scaleBase')};
  background: linear-gradient(180deg, rgba(250, 251, 255, 0) 0%, #fafbff 100%);
  z-index: ${themeGet('zIndex.zIndex03')};

  a,
  button {
    ${themeGet('font.whitneyMedium')}
    border-radius: 4px;
    padding: 8px 16px!important;
    font-size: ${themeGet('scale.scaleUp01')};
    background: ${themeGet('color.colorBlue50')};
    display: block;
    width: 100%;
    border: none;
    color: ${themeGet('color.colorWhite')};
    transition: background 0.2s linear;
    cursor: pointer;
  }

  a {
    border: ${themeGet('scale.scaleDown05')} solid
      ${themeGet('color.colorBlue50')};
    background: ${themeGet('color.colorWhite')};
    text-decoration: none;
    color: ${themeGet('color.colorBlue50')};
    padding: ${themeGet('scale.scaleDown02')};
    transition: border 0.2s linear, color 0.2s linear;
  }

  button:hover {
    background: ${themeGet('color.colorBlue70')};
  }

  a:hover {
    color: ${themeGet('color.colorBlue70')};
    border-color: ${themeGet('color.colorBlue70')};
  }

  @media (min-width: ${themeGet('breakpoint.breakpointMobile')}) {
    padding: ${themeGet('scale.scaleUp07')};
  }

  @media (min-width: ${themeGet('breakpoint.breakpointNavMd')}) {
    position: static;
    padding: 0;
    padding-right: ${themeGet('scale.scaleUp05')};
    background: none;
    display: flex;
    align-items: center;

    a,
    button {
      font-size: ${themeGet('scale.scaleDown01')};
      padding: ${themeGet('scale.scaleDown03')} ${themeGet('scale.scaleUp01')};
    }

    a {
      padding: ${themeGet('scale.scaleDown03')} ${themeGet('scale.scaleUp02')};
    }
  }

  @media (min-width: ${themeGet('breakpoint.breakpointNavLg')}) {
    a,
    button {
      font-size: ${themeGet('scale.scaleBase')};
      padding: ${themeGet('scale.scaleDown02')} ${themeGet('scale.scaleUp03')};
    }
  }

  @media (min-width: ${themeGet('breakpoint.breakpointNavXl')}) {
    padding-right: ${themeGet('scale.scaleUp03')};
  }
`;

export const StyledNavigation = styled.nav`
  position: sticky;
  top: 0;
  background: ${themeGet('color.colorGrayBlue10')};
  z-index: ${themeGet('zIndex.zIndex09')};
  display: grid;
  grid-template-rows: repeat(3, max-content);
  grid-template-columns: auto min-content;
  grid-template-areas: 'logo menu-button' 'main-menu main-menu' 'secondary-menu secondary-menu';

  &.is-mobile-open {
    height: 100vh;
    position: fixed;
    width: 100%;
    overflow-y: scroll;

    ${StyledMainMenu},
    ${StyledSecondaryMenu},
    ${StyledCta} {
      display: block;
    }
  }

  ${StyledMenuItem} + ${StyledMenuItem},
  ${StyledSecondaryMenu} ${StyledMenuItem}:first-of-type {
    &::before {
      content: '';
      display: block;
      height: 1px;
      margin: -0.5px ${themeGet('scale.scaleUp03')};
    }
  }

  ${StyledMenuItem}:not(.is-open):not(.is-active) + ${StyledMenuItem}:not(.is-open):not(.is-active),
  ${StyledSecondaryMenu} ${StyledMenuItem}:first-of-type:not(.is-open):not(.is-active):not(.is-previous-item-active) {
    &::before {
      background: ${themeGet('color.colorGray300')};
    }
  }

  @media (min-width: ${themeGet('breakpoint.breakpointMobile')}) {
    ${StyledMenuItem} + ${StyledMenuItem},
    ${StyledSecondaryMenu} ${StyledMenuItem}:first-of-type {
      &::before {
        margin: -0.5px ${themeGet('scale.scaleUp07')};
      }
    }
  }

  @media (min-width: ${themeGet('breakpoint.breakpointNavMd')}) {
    grid-template-columns: max-content auto max-content max-content;
    grid-template-areas: 'logo main-menu secondary-menu cta';
    background: ${themeGet('color.colorWhite')};

    ${StyledMenuItem} + ${StyledMenuItem},
    ${StyledSecondaryMenu} ${StyledMenuItem}:first-of-type {
      &::before {
        display: none;
      }
    }
  }
`;

export const StyledMaintenanceNavigation = styled.nav`
  position: sticky;
  top: 0;
  background: ${themeGet('color.colorWhite')};
  z-index: ${themeGet('zIndex.zIndex09')};
  display: grid;
  grid-template-columns: auto max-content max-content;
  grid-template-areas: 'logo secondary-menu cta';

  ${StyledCta} {
    display: flex;
    position: static;
    padding: 0;
    padding-right: ${themeGet('scale.scaleUp05')};
    background: none;
    align-items: center;

    a,
    button {
      font-size: ${themeGet('scale.scaleDown01')};
      padding: ${themeGet('scale.scaleDown03')} ${themeGet('scale.scaleUp01')};
    }

    a {
      padding: ${themeGet('scale.scaleDown03')} ${themeGet('scale.scaleUp02')};
    }
  }

  ${StyledSecondaryMenu} {
    display: flex;
    padding-bottom: 0;
  }

  ${StyledMenuItem} {
    align-content: center;
    position: static;
    margin-right: ${themeGet('scale.scaleUp02')};
    height: 100%;

    > a {
      height: 100%;
      padding: 0;
      display: flex;
      align-items: center;
      font-size: ${themeGet('scale.scaleDown01')};
      padding-top: ${themeGet('scale.scaleDown04')};
      border-bottom: ${themeGet('scale.scaleDown04')} solid transparent;
    }

    a:hover {
      background: none;
      border-bottom-color: ${themeGet('color.colorBlue50')};
      color: ${themeGet('color.colorBlue50')};
    }
  }

  @media (min-width: ${themeGet('breakpoint.breakpointNavLg')}) {
    ${StyledMenuItem} > a {
      font-size: ${themeGet('scale.scaleBase')};
    }

    ${StyledCta} {
      a,
      button {
        font-size: ${themeGet('scale.scaleBase')};
        padding: ${themeGet('scale.scaleDown02')} ${themeGet('scale.scaleUp03')};
      }
    }
  }

  @media (min-width: ${themeGet('breakpoint.breakpointNavXl')}) {
    ${StyledMenuItem} {
      margin-right: ${themeGet('scale.scaleUp05')};
    }

    ${StyledCta} {
      padding-right: ${themeGet('scale.scaleUp10')};
    }
  }
`;
